'use client';

import { c as _c } from "react/compiler-runtime";
import * as Headless from '@headlessui/react';
import NextLink from 'next/link';
import type React from 'react';
import { forwardRef } from 'react';
import { cn } from '../../shared/cn';
export type LinkProps = React.ComponentPropsWithoutRef<'a'> & {
  href: string;
  /**
   * Client-side prefetch of the target route. Defaults to `false`: pages are
   * `force-dynamic` on a single VPS (no CDN), so prefetching every in-viewport
   * link would hammer the server. Opt in per-link where snappy nav matters.
   */
  prefetch?: boolean;
};

/**
 * App-internal navigation link. Wraps `next/link` so same-origin hrefs do a
 * client-side (soft) navigation instead of a full page reload. External,
 * `mailto:`, `tel:` and hash hrefs fall back to normal anchor behaviour.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(t0, ref) {
  const $ = _c(13);
  let className;
  let href;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      href,
      prefetch: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = href;
    $[3] = props;
    $[4] = t1;
  } else {
    className = $[1];
    href = $[2];
    props = $[3];
    t1 = $[4];
  }
  const prefetch = t1 === undefined ? false : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("cursor-pointer text-contentPrimary", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== href || $[8] !== prefetch || $[9] !== props || $[10] !== ref || $[11] !== t2) {
    t3 = <Headless.DataInteractive as={NextLink} {...props} href={href} prefetch={prefetch} ref={ref} className={t2} />;
    $[7] = href;
    $[8] = prefetch;
    $[9] = props;
    $[10] = ref;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
});